import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleHalfStroke,
  faCircleXmark,
  faCircleQuestion,
  faTriangleExclamation,
  faUserGraduate,
  faCommentDots,
  faArrowUpRightFromSquare,
  faRotate,
  faPaperPlane,
  faPenToSquare,
  faTrashCan,
  faXmark,
  faVideo,
  faStar,
  faChalkboardUser,
  faWandMagicSparkles,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import './FeedbackApprove.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

/* ---------- Pay codes (same as before) ---------- */
const PAY = {
  0: { label: 'Full pay', tone: 'full', icon: faCircleCheck },
  1: { label: 'Half pay', tone: 'half', icon: faCircleHalfStroke },
  2: { label: 'No pay', tone: 'none', icon: faCircleXmark },
};
const payOf = (v) => PAY[v] || { label: 'Not set', tone: 'unknown', icon: faCircleQuestion };

const TEACHER_TARGET = 50; // minutes
const STUDENT_TARGET = 30;

const durationTone = (seconds, who) => {
  const min = (seconds || 0) / 60;
  if (!min) return 'zero';
  if (who === 'teacher') {
    if (min >= TEACHER_TARGET) return 'good';
    if (min >= 30) return 'warn';
    return 'bad';
  }
  return min >= STUDENT_TARGET ? 'good' : 'warn';
};

/* seconds -> MM:SS (same as before) */
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/* MM:SS -> seconds (same as before) */
const parseDurationToSeconds = (durationStr) => {
  if (!durationStr || durationStr === '00:00') return 0;
  const parts = durationStr.split(':');
  if (parts.length === 2) {
    const mins = parseInt(parts[0], 10) || 0;
    const secs = parseInt(parts[1], 10) || 0;
    return mins * 60 + secs;
  }
  return parseInt(durationStr, 10) || 0;
};

const msToClock = (ms) => {
  if (!ms || ms < 0 || Number.isNaN(ms)) return '00:00:00';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
};

const spanMs = (t) => (t?.JoinTime && t?.LeaveTime ? new Date(t.LeaveTime) - new Date(t.JoinTime) : 0);

const fmtDay = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const fmtTime = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const fmtClock = (s) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const dayLabel = (d) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(today)) return 'Today';
  if (dayKey(d) === dayKey(yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

const isBlank = (v) => v === null || v === undefined || v === '';
const money = (v) => (isBlank(v) ? null : `$${parseFloat(v) || 0}`);

/* Same message as before, per pay type */
const generateAutoEvaluation = (paymentType) => {
  switch (paymentType) {
    case 0:
      return '✅ Session completed';
    case 1:
      return '⚠️ Duration detected';
    case 2:
      return '❌ Duration not met';
    default:
      return '⚠️ Needs manual review';
  }
};

/* =========================================================
   Pay decision from Zoom data — UNCHANGED logic from the old file
   ========================================================= */
// total time of several join/leave periods, counting overlaps once (two devices, rejoins)
const unionMs = (times) => {
  const list = (times || [])
    .filter((t) => t.JoinTime && t.LeaveTime)
    .map((t) => [new Date(t.JoinTime).getTime(), new Date(t.LeaveTime).getTime()])
    .filter(([a, b]) => b > a)
    .sort((x, y) => x[0] - y[0]);
  let total = 0;
  let cur = null;
  list.forEach(([a, b]) => {
    if (!cur || a > cur[1]) {
      if (cur) total += cur[1] - cur[0];
      cur = [a, b];
    } else cur[1] = Math.max(cur[1], b);
  });
  if (cur) total += cur[1] - cur[0];
  return total;
};

const determinePaymentType = (zoomData, feedback) => {
  let paymentType = 0; // 0=Full Pay, 1=Half Pay, 2=No Pay
  let teacherTotalMs = 0;
  let studentTotalMs = 0;

  if (!zoomData || zoomData.length === 0) {
    return {
      paymentType: feedback.fullpay !== null ? feedback.fullpay : 2,
      teacherDuration: feedback.TeacherDuration || 0,
      studentDuration: feedback.StudentDuration || 0,
    };
  }

  let teacherJoined = false;
  let studentJoined = false;
  let teacherFound = false;
  let studentFound = false;

  const teacherFirstName = feedback.TFirstName?.toLowerCase() || '';
  const teacherLastName = feedback.TLastName?.toLowerCase() || '';
  const studentFirstName = feedback.FirstName?.toLowerCase() || '';
  const studentLastName = feedback.LastName?.toLowerCase() || '';
  const teacherFullName = `${teacherFirstName} ${teacherLastName}`.trim();
  const studentFullName = `${studentFirstName} ${studentLastName}`.trim();

  // First pass: match by name. A person can be in the list more than once (rejoined as "MONA" then "Mona",
  // or from two devices): all their times are added together, overlaps counted once.
  const teacherTimes = [];
  const studentTimes = [];
  zoomData.forEach((participant) => {
    const displayName = participant.DisplayName?.toLowerCase() || '';
    const email = participant.Email?.toLowerCase() || '';
    const joinTimes = participant.JoinTimes || [];

    let totalMs = 0;
    joinTimes.forEach((time) => {
      if (time.JoinTime && time.LeaveTime) {
        totalMs += new Date(time.LeaveTime) - new Date(time.JoinTime);
      }
    });

    const isTeacher =
      (teacherFirstName && displayName.includes(teacherFirstName)) ||
      (teacherLastName && displayName.includes(teacherLastName)) ||
      (teacherFullName && displayName.includes(teacherFullName)) ||
      (teacherFirstName && email.includes(teacherFirstName)) ||
      (teacherLastName && email.includes(teacherLastName)) ||
      displayName.includes('teacher') ||
      displayName.includes('instructor') ||
      displayName.includes('tutor') ||
      displayName.includes('professor');

    const isStudent =
      (studentFirstName && displayName.includes(studentFirstName)) ||
      (studentLastName && displayName.includes(studentLastName)) ||
      (studentFullName && displayName.includes(studentFullName)) ||
      (studentFirstName && email.includes(studentFirstName)) ||
      (studentLastName && email.includes(studentLastName)) ||
      displayName.includes('student') ||
      displayName.includes('learner') ||
      displayName.includes('participant');

    if (isTeacher && !isStudent) {
      teacherTimes.push(...joinTimes);
      teacherJoined = teacherJoined || joinTimes.length > 0;
      teacherFound = true;
    } else if (isStudent && !isTeacher) {
      studentTimes.push(...joinTimes);
      studentJoined = studentJoined || joinTimes.length > 0;
      studentFound = true;
    }
  });
  if (teacherFound) teacherTotalMs = unionMs(teacherTimes);
  if (studentFound) studentTotalMs = unionMs(studentTimes);

  // Teacher not found: longest = teacher, second = student
  if (!teacherFound && zoomData.length > 0) {
    const sortedParticipants = [...zoomData]
      .map((p) => {
        const duration = p.JoinTimes.reduce((sum, t) => {
          if (t.JoinTime && t.LeaveTime) return sum + (new Date(t.LeaveTime) - new Date(t.JoinTime));
          return sum;
        }, 0);
        return { ...p, duration };
      })
      .sort((a, b) => b.duration - a.duration);

    if (sortedParticipants.length > 0) {
      const longest = sortedParticipants[0];
      teacherTotalMs = longest.duration;
      teacherJoined = longest.JoinTimes.length > 0;
      teacherFound = true;

      if (sortedParticipants.length > 1) {
        const secondLongest = sortedParticipants[1];
        if (secondLongest.duration > 0) {
          studentTotalMs = secondLongest.duration;
          studentJoined = secondLongest.JoinTimes.length > 0;
          studentFound = true;
        }
      }
    }
  }

  // Student not found: second longest with some time
  if (!studentFound && zoomData.length > 0) {
    const remainingParticipants = zoomData.filter((p) => {
      const duration = p.JoinTimes.reduce((sum, t) => {
        if (t.JoinTime && t.LeaveTime) return sum + (new Date(t.LeaveTime) - new Date(t.JoinTime));
        return sum;
      }, 0);
      return duration > 0;
    });

    if (remainingParticipants.length > 0) {
      const sorted = remainingParticipants.sort((a, b) => {
        const durA = a.JoinTimes.reduce((sum, t) => sum + (new Date(t.LeaveTime) - new Date(t.JoinTime)), 0);
        const durB = b.JoinTimes.reduce((sum, t) => sum + (new Date(t.LeaveTime) - new Date(t.JoinTime)), 0);
        return durB - durA;
      });

      if (sorted.length === 1) {
        studentJoined = false;
        studentTotalMs = 0;
      } else {
        const student = sorted[1];
        studentTotalMs = student.JoinTimes.reduce((sum, t) => {
          if (t.JoinTime && t.LeaveTime) return sum + (new Date(t.LeaveTime) - new Date(t.JoinTime));
          return sum;
        }, 0);
        studentJoined = student.JoinTimes.length > 0;
      }
    }
  }

  const teacherMinutes = teacherTotalMs / (1000 * 60);
  const studentMinutes = studentTotalMs / (1000 * 60);

  if (teacherJoined || studentJoined) {
    if (teacherMinutes >= 50 && studentMinutes >= 30) {
      paymentType = 0;
    } else if (teacherMinutes >= 50 && studentMinutes > 0 && studentMinutes < 30) {
      paymentType = 1;
    } else if (teacherMinutes < 50 && teacherMinutes >= 30 && studentMinutes >= 30) {
      paymentType = 1;
    } else if (teacherMinutes >= 30 && studentMinutes === 0 && studentJoined === false) {
      paymentType = 1;
    } else if (teacherMinutes < 30 && studentMinutes === 0 && studentJoined === false) {
      paymentType = 2;
    } else if (teacherMinutes < 30 && studentMinutes > 0) {
      paymentType = 2;
    } else {
      paymentType = 2;
    }
  } else {
    paymentType = 2;
  }

  // durations in SECONDS
  return {
    paymentType,
    teacherDuration: Math.round(teacherTotalMs / 1000),
    studentDuration: Math.round(studentTotalMs / 1000),
  };
};

/* Same starting values as before for each loaded feedback */
const initList = (data) =>
  (data?.pendingFeedbacks || [])
    .map((fb) => ({
      ...fb,
      TeacherDuration: 0,
      StudentDuration: 0,
      AdminEvaluation: fb.AdminEvaluation || '',
      fullpay: fb.fullpay !== null ? fb.fullpay : 2,
    }))
    .sort((a, b) => new Date(a.ClassDateTime) - new Date(b.ClassDateTime)); // oldest first

const tName = (f) => [f.TFirstName, f.TLastName].filter(Boolean).join(' ') || 'Unknown teacher';

const needsReview = (f) => f.fullpay !== 0 || f.isAutoProcessed === false || isBlank(f.SessionRate) || isBlank(f.SessionFees);

/* ---------- Small pieces ---------- */
const Ring = ({ who, seconds }) => {
  const target = who === 'teacher' ? TEACHER_TARGET : STUDENT_TARGET;
  const ratio = Math.min(1, (seconds || 0) / 60 / target);
  const r = 15;
  const c = 2 * Math.PI * r;
  return (
    <div className={`rmafa-time is-${durationTone(seconds, who)}`}>
      <svg viewBox="0 0 36 36" aria-hidden="true">
        <circle className="rmafa-ring-bg" cx="18" cy="18" r={r} />
        <circle className="rmafa-ring" cx="18" cy="18" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - ratio)} />
      </svg>
      <div>
        <span className="rmafa-time-label">{who === 'teacher' ? 'Teacher' : 'Student'}</span>
        <strong>{formatDuration(seconds)}</strong>
      </div>
    </div>
  );
};

const Photo = ({ src, big }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className={`rmafa-photo${big ? ' is-big' : ''}`}>
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : <FontAwesomeIcon icon={faUserGraduate} />}
    </span>
  );
};

/* Zoom attendance: one bar per participant on a shared timeline */
const Attendance = ({ data }) => {
  const all = data.flatMap((p) => (p.JoinTimes || []).filter((t) => t.JoinTime && t.LeaveTime));
  if (!all.length) return <p className="rmafa-muted">No join / leave times in the Zoom report.</p>;
  const start = Math.min(...all.map((t) => new Date(t.JoinTime).getTime()));
  const end = Math.max(...all.map((t) => new Date(t.LeaveTime).getTime()));
  const range = Math.max(1, end - start);

  return (
    <div className="rmafa-att">
      <div className="rmafa-att-scale" aria-hidden="true">
        <span>{fmtClock(start)}</span>
        <span>{fmtClock(end)}</span>
      </div>
      {data.map((p, i) => {
        const times = p.JoinTimes || [];
        const total = times.reduce((s, t) => s + spanMs(t), 0);
        return (
          <div key={`${p.DisplayName}-${i}`} className="rmafa-att-row">
            <div className="rmafa-att-head">
              <strong>{p.DisplayName || 'Unknown'}</strong>
              <span>{msToClock(total)}</span>
            </div>
            <div className="rmafa-att-track" aria-hidden="true">
              {times
                .filter((t) => t.JoinTime && t.LeaveTime)
                .map((t, k) => {
                  const left = ((new Date(t.JoinTime) - start) / range) * 100;
                  const width = Math.max(0.8, (spanMs(t) / range) * 100);
                  return <i key={k} style={{ left: `${left}%`, width: `${width}%` }} />;
                })}
            </div>
            <ul className="rmafa-att-list">
              {times.map((t, k) => (
                <li key={k}>
                  {fmtClock(t.JoinTime)} → {fmtClock(t.LeaveTime)}
                  <b>{msToClock(spanMs(t))}</b>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const EMPTY_FORM = {
  fullpay: null,
  SessionRate: '',
  SessionFees: '',
  TeacherDuration: '00:00',
  StudentDuration: '00:00',
  AdminEvaluation: '',
};

/* =========================================================
   Component
   ========================================================= */
const FeedbackApprove = ({ userData, onSubmitrefresh }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  const [progress, setProgress] = useState(null); // { current, total } while checking Zoom
  const [checkingId, setCheckingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [teacher, setTeacher] = useState('all');
  const [filter, setFilter] = useState('all');

  const [review, setReview] = useState(null); // feedback being reviewed
  const [form, setForm] = useState(EMPTY_FORM);
  const [zoom, setZoom] = useState({ loading: false, data: null });
  const [deleting, setDeleting] = useState(false);

  const [confirm, setConfirm] = useState(null); // { title, body, label, tone, onYes }
  const [dialog, setDialog] = useState(null); // { tone, title, body }
  const [toast, setToast] = useState('');

  const runRef = useRef(0); // latest Zoom check run
  const zoomReq = useRef(0); // latest modal Zoom request
  const adminId = userData?.id;
  const checking = !!progress;

  // same endpoint as before
  const fetchList = useCallback(async () => {
    const res = await axios.get(`${API}/api/admin/adminfeedbackapprove/adminfeedbackapprove`, auth());
    return initList(res.data);
  }, []);

  // ---------- Auto-check every feedback against Zoom (same calls + same results) ----------
  const autoProcess = useCallback(async (list) => {
    if (!list || list.length === 0) return;
    const run = ++runRef.current;
    const ids = new Set(list.map((f) => f.Id));
    setFeedbacks((prev) => prev.map((f) => (ids.has(f.Id) ? { ...f, queued: true } : f)));
    setProgress({ current: 0, total: list.length });

    for (let i = 0; i < list.length; i += 1) {
      if (runRef.current !== run) return; // a newer run started, or we left the tab
      const feedback = list[i];
      setProgress({ current: i + 1, total: list.length });
      setCheckingId(feedback.Id);
      let next;
      try {
        const zoomResponse = await axios.get(
          `${API}/api/admin/adminfeedbackapprove/adminfeedbackapprove/zoom/${feedback.Id}`,
          auth()
        );
        const { paymentType, teacherDuration, studentDuration } = determinePaymentType(zoomResponse.data.zoomData, feedback);
        next = {
          ...feedback,
          fullpay: paymentType,
          SessionRate: feedback.SessionRate || 25,
          SessionFees: feedback.SessionFees || 25,
          TeacherDuration: teacherDuration, // seconds
          StudentDuration: studentDuration, // seconds
          AdminEvaluation: generateAutoEvaluation(paymentType),
          isAutoProcessed: true,
          isEdited: false,
          queued: false,
        };
      } catch (err) {
        console.error(`Error processing feedback ${feedback.Id}:`, err);
        next = {
          ...feedback,
          AdminEvaluation: feedback.AdminEvaluation || '⚠️ Zoom data unavailable',
          isAutoProcessed: false,
          queued: false,
        };
      }
      if (runRef.current !== run) return;
      setFeedbacks((prev) => prev.map((f) => (f.Id === next.Id ? next : f)));
    }

    if (runRef.current === run) {
      setProgress(null);
      setCheckingId(null);
    }
  }, []);

  // stop any running check when the tab closes
  useEffect(() => {
    const run = runRef;
    const req = zoomReq;
    return () => {
      run.current += 1;
      req.current += 1;
    };
  }, []);

  // ---------- Load ----------
  useEffect(() => {
    if (!adminId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await fetchList();
        if (cancelled) return;
        setFeedbacks(list);
        setError('');
        setLoading(false);
        if (list.length) autoProcess(list);
      } catch (err) {
        console.error('Error fetching feedbacks:', err);
        if (!cancelled) {
          setError('We couldn’t load the feedbacks waiting for approval.');
          setFeedbacks([]);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminId, reload, fetchList, autoProcess]);

  // toast hides itself
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ---------- Re-check Zoom ----------
  const editedCount = feedbacks.filter((f) => f.isEdited).length;
  const askRecheck = () => {
    if (!editedCount) {
      autoProcess(feedbacks);
      return;
    }
    setConfirm({
      tone: 'warn',
      title: 'Re-check every class?',
      body: `You changed ${editedCount} ${editedCount === 1 ? 'class' : 'classes'} by hand. Re-checking Zoom will replace those changes.`,
      label: 'Re-check anyway',
      onYes: () => {
        setConfirm(null);
        autoProcess(feedbacks);
      },
    });
  };

  // ---------- Submit all (same bulk endpoint + same payload) ----------
  const submitAll = async () => {
    setConfirm(null);
    setSubmitting(true);
    try {
      const bulkData = feedbacks.map((feedback) => ({
        IdFeedback: feedback.Id,
        fullpay: feedback.fullpay !== null ? feedback.fullpay : 2,
        SessionRate: parseFloat(feedback.SessionRate) || 0,
        SessionFees: parseFloat(feedback.SessionFees) || 0,
        TeacherDuration: Math.round(feedback.TeacherDuration || 0),
        StudentDuration: Math.round(feedback.StudentDuration || 0),
        AdminEvaluation: feedback.AdminEvaluation || '',
      }));

      const response = await axios.post(`${API}/api/admin/approvefeedback/bulk`, { feedbacks: bulkData }, auth());

      if (response.data.success) {
        const successful = response.data.results?.successful || [];
        const failed = response.data.results?.failed || [];

        let fresh = [];
        try {
          fresh = await fetchList();
          setFeedbacks(fresh);
        } catch (err) {
          console.error('Error reloading feedbacks:', err);
        }
        try {
          if (onSubmitrefresh) await onSubmitrefresh();
        } catch (err) {
          console.error('Error refreshing counts:', err);
        }

        setDialog({
          tone: failed.length ? 'warn' : 'success',
          title: failed.length ? 'Submitted with some errors' : 'All submitted',
          body: `${successful.length} ${successful.length === 1 ? 'feedback was' : 'feedbacks were'} approved.${
            failed.length ? ` ${failed.length} failed. They stay in the list so you can try again.` : ''
          }`,
        });
        if (fresh.length) autoProcess(fresh);
      } else {
        setDialog({ tone: 'error', title: 'Not submitted', body: 'Failed to submit feedbacks. Please try again.' });
      }
    } catch (err) {
      console.error('Error submitting feedbacks:', err);
      setDialog({ tone: 'error', title: 'Not submitted', body: 'Failed to submit feedbacks. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const askSubmit = () => {
    const n = feedbacks.length;
    const flagged = feedbacks.filter(needsReview).length;
    setConfirm({
      tone: 'info',
      title: `Submit ${n} ${n === 1 ? 'feedback' : 'feedbacks'}?`,
      body: flagged
        ? `${flagged} of them still look off (not full pay, no Zoom data, or no rate). They’ll be submitted as they are.`
        : 'Every class is on full pay with Zoom data.',
      label: 'Submit all',
      onYes: submitAll,
    });
  };

  // ---------- Review ----------
  const openReview = async (feedback) => {
    setReview(feedback);
    setForm({
      fullpay: feedback.fullpay !== null ? feedback.fullpay : 2,
      SessionRate: feedback.SessionRate || 25,
      SessionFees: feedback.SessionFees || 25,
      TeacherDuration: formatDuration(feedback.TeacherDuration || 0),
      StudentDuration: formatDuration(feedback.StudentDuration || 0),
      AdminEvaluation: feedback.AdminEvaluation || '',
    });

    const req = ++zoomReq.current;
    setZoom({ loading: true, data: null });
    try {
      const response = await axios.get(
        `${API}/api/admin/adminfeedbackapprove/adminfeedbackapprove/zoom/${feedback.Id}`,
        auth()
      );
      if (zoomReq.current === req) setZoom({ loading: false, data: response.data.zoomData });
    } catch (err) {
      console.error('Error fetching Zoom data:', err);
      if (zoomReq.current === req) setZoom({ loading: false, data: null });
    }
  };

  const closeReview = useCallback(() => {
    zoomReq.current += 1;
    setReview(null);
    setZoom({ loading: false, data: null });
    setForm(EMPTY_FORM);
  }, []);

  const onField = (e) => {
    const { name, value, type } = e.target;
    if (type === 'radio') {
      setForm((f) => ({ ...f, [name]: parseInt(value, 10) }));
    } else if (name === 'TeacherDuration' || name === 'StudentDuration') {
      // MM:SS (up to 999 minutes)
      if (value === '' || /^\d{0,3}:?\d{0,2}$/.test(value)) setForm((f) => ({ ...f, [name]: value }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  // Save in the list only (same as before) — submit all sends it
  const saveReview = () => {
    if (!review) return;
    const teacherSeconds = parseDurationToSeconds(form.TeacherDuration);
    const studentSeconds = parseDurationToSeconds(form.StudentDuration);
    setFeedbacks((prev) =>
      prev.map((fb) =>
        fb.Id === review.Id
          ? {
              ...fb,
              fullpay: form.fullpay,
              SessionRate: parseFloat(form.SessionRate) || 0,
              SessionFees: parseFloat(form.SessionFees) || 0,
              TeacherDuration: teacherSeconds,
              StudentDuration: studentSeconds,
              AdminEvaluation: form.AdminEvaluation,
              isEdited: true,
            }
          : fb
      )
    );
    closeReview();
    setToast('Changes saved. Submit all when you’re ready.');
  };

  // Zoom suggestion inside the review (same rules)
  const suggestion = review && zoom.data?.length ? determinePaymentType(zoom.data, review) : null;
  const useSuggestion = () => {
    if (!suggestion) return;
    setForm((f) => ({
      ...f,
      fullpay: suggestion.paymentType,
      TeacherDuration: formatDuration(suggestion.teacherDuration),
      StudentDuration: formatDuration(suggestion.studentDuration),
      AdminEvaluation: generateAutoEvaluation(suggestion.paymentType),
    }));
  };

  // ---------- Delete (same endpoint) ----------
  const deleteFeedback = async () => {
    if (!review) return;
    const target = review;
    setConfirm(null);
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/api/admin/approvefeedback/deletefeedback/${target.Id}`, auth());
      if (response.data.success) {
        // remove it here so your changes on the other classes stay
        setFeedbacks((prev) => prev.filter((f) => f.Id !== target.Id));
        try {
          if (onSubmitrefresh) await onSubmitrefresh();
        } catch (err) {
          console.error('Error refreshing counts:', err);
        }
        closeReview();
        setToast('Feedback deleted.');
      } else {
        setDialog({ tone: 'error', title: 'Not deleted', body: response.data.message || 'Failed to delete feedback.' });
      }
    } catch (err) {
      console.error('Error deleting feedback:', err);
      setDialog({
        tone: 'error',
        title: 'Not deleted',
        body: `Failed to delete feedback: ${err.response?.data?.message || err.message}`,
      });
    } finally {
      setDeleting(false);
    }
  };

  const askDelete = () =>
    setConfirm({
      tone: 'error',
      title: 'Delete this feedback?',
      body: `The feedback for ${[review?.FirstName, review?.LastName].filter(Boolean).join(' ') || 'this student'} will be removed. This can’t be undone.`,
      label: 'Delete',
      onYes: deleteFeedback,
    });

  // Escape closes the top layer
  useEffect(() => {
    if (!review && !confirm && !dialog) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (dialog) setDialog(null);
      else if (confirm) setConfirm(null);
      else if (!deleting) closeReview();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [review, confirm, dialog, deleting, closeReview]);

  /* ---------- Derived ---------- */
  const teacherList = (() => {
    const map = {};
    feedbacks.forEach((f) => {
      const n = tName(f);
      if (!map[n]) map[n] = { name: n, total: 0, flagged: 0 };
      map[n].total += 1;
      if (!f.queued && needsReview(f)) map[n].flagged += 1;
    });
    return Object.values(map).sort((a, b) => b.flagged - a.flagged || b.total - a.total);
  })();

  const scoped = teacher === 'all' ? feedbacks : feedbacks.filter((f) => tName(f) === teacher);
  const flagged = scoped.filter((f) => !f.queued && needsReview(f));
  const shown = filter === 'review' ? flagged : scoped;

  const totals = { full: 0, half: 0, none: 0, unknown: 0 };
  scoped.forEach((f) => {
    if (!f.queued) totals[payOf(f.fullpay).tone] += 1;
  });

  const groups = [];
  shown.forEach((f) => {
    const d = new Date(f.ClassDateTime);
    const valid = !Number.isNaN(d.getTime());
    const key = valid ? dayKey(d) : 'unknown';
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: valid ? dayLabel(d) : 'Unknown date', items: [] };
      groups.push(g);
    }
    g.items.push(f);
  });

  // keep the teacher filter valid after submit / delete
  useEffect(() => {
    if (teacher !== 'all' && !feedbacks.some((f) => tName(f) === teacher)) setTeacher('all');
  }, [feedbacks, teacher]);

  /* ---------- Render ---------- */
  if (loading) {
    return (
      <div className="rmafa" aria-busy="true">
        <div className="rmafa-skel rmafa-skel--hero" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="rmafa-skel" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmafa">
        <div className="rmafa-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmafa-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const total = feedbacks.length;
  const reviewPay = payOf(form.fullpay);
  const tSec = parseDurationToSeconds(form.TeacherDuration);
  const sSec = parseDurationToSeconds(form.StudentDuration);

  return (
    <div className="rmafa">
      {/* ---------- Summary + actions ---------- */}
      <section className="rmafa-hero" aria-label="Feedbacks waiting for approval">
        <div className="rmafa-score">
          <strong>{total}</strong>
          <p>{total === 1 ? 'class waiting for approval' : 'classes waiting for approval'}</p>
        </div>

        <div className="rmafa-actions">
          <button type="button" className="rmafa-btn rmafa-btn--ghost" onClick={askRecheck} disabled={checking || submitting || total === 0}>
            <FontAwesomeIcon icon={faRotate} spin={checking} />
            {checking ? 'Checking Zoom…' : 'Re-check Zoom'}
          </button>
          <button type="button" className="rmafa-btn" onClick={askSubmit} disabled={submitting || checking || total === 0}>
            <FontAwesomeIcon icon={faPaperPlane} />
            {submitting ? 'Submitting…' : `Submit all (${total})`}
          </button>
        </div>

        {total > 0 && (
          <div className="rmafa-split">
            {checking ? (
              <>
                <div className="rmafa-progress" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.current} aria-label="Checking Zoom">
                  <span style={{ width: `${(progress.current / progress.total) * 100}%` }} />
                </div>
                <p className="rmafa-split-note">
                  Checking Zoom {progress.current} of {progress.total}…
                </p>
              </>
            ) : (
              <>
                <div className="rmafa-bar" aria-hidden="true">
                  {['full', 'half', 'none', 'unknown'].map(
                    (t) => totals[t] > 0 && <span key={t} className={`is-${t}`} style={{ flexGrow: totals[t] }} />
                  )}
                </div>
                <ul className="rmafa-legend">
                  <li className="is-full">
                    <i /> Full <b>{totals.full}</b>
                  </li>
                  <li className="is-half">
                    <i /> Half <b>{totals.half}</b>
                  </li>
                  <li className="is-none">
                    <i /> No pay <b>{totals.none}</b>
                  </li>
                  <li className="rmafa-legend-hint">
                    <FontAwesomeIcon icon={faCircleInfo} /> Zoom checked. Review anything that looks off, then submit all.
                  </li>
                </ul>
              </>
            )}
          </div>
        )}
      </section>

      {total === 0 ? (
        <div className="rmafa-empty">
          <FontAwesomeIcon icon={faCircleCheck} className="is-done" />
          <p>Nothing waiting for approval. All caught up!</p>
        </div>
      ) : (
        <>
          {/* ---------- Teacher filter ---------- */}
          {teacherList.length > 1 && (
            <div className="rmafa-teachers" role="radiogroup" aria-label="Teacher">
              <button type="button" role="radio" aria-checked={teacher === 'all'} className={teacher === 'all' ? 'is-on' : ''} onClick={() => setTeacher('all')}>
                All teachers
              </button>
              {teacherList.map((tc) => (
                <button
                  key={tc.name}
                  type="button"
                  role="radio"
                  aria-checked={teacher === tc.name}
                  className={teacher === tc.name ? 'is-on' : ''}
                  onClick={() => setTeacher(tc.name)}
                >
                  {tc.name}
                  <span className={tc.flagged ? 'is-alert' : ''}>{tc.flagged || tc.total}</span>
                </button>
              ))}
            </div>
          )}

          {/* ---------- Tabs ---------- */}
          <div className="rmafa-tabs" role="tablist" aria-label="Filter feedbacks">
            <button type="button" role="tab" aria-selected={filter === 'all'} className={filter === 'all' ? 'is-on' : ''} onClick={() => setFilter('all')}>
              All <span>{scoped.length}</span>
            </button>
            <button type="button" role="tab" aria-selected={filter === 'review'} className={filter === 'review' ? 'is-on' : ''} onClick={() => setFilter('review')}>
              Needs review <span className={flagged.length ? 'is-alert' : ''}>{flagged.length}</span>
            </button>
          </div>

          {/* ---------- List ---------- */}
          {groups.length === 0 ? (
            <div className="rmafa-empty">
              <FontAwesomeIcon icon={faCircleCheck} className="is-done" />
              <p>Everything here looks good.</p>
            </div>
          ) : (
            groups.map((g) => (
              <section key={g.key} className="rmafa-day">
                <h3 className="rmafa-day-title">{g.label}</h3>
                <ul className="rmafa-list">
                  {g.items.map((f) => {
                    const d = new Date(f.ClassDateTime);
                    const valid = !Number.isNaN(d.getTime());
                    const pay = payOf(f.fullpay);
                    const busy = f.queued;
                    const rate = money(f.SessionRate);
                    const fees = money(f.SessionFees);
                    return (
                      <li key={f.Id} className={`rmafa-row${busy ? ' is-busy' : ''}`}>
                        <div className="rmafa-student">
                          <Photo src={f.Image} />
                          <div className="rmafa-who">
                            <h4>
                              <span>{[f.FirstName, f.LastName].filter(Boolean).join(' ') || 'Student'}</span>
                              {f.IdUser && (
                                <a href={`/Admin/studentprofile/${f.IdUser}`} target="_blank" rel="noopener noreferrer" aria-label="Open student profile">
                                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                </a>
                              )}
                            </h4>
                            <p>
                              {valid ? fmtTime(d) : '—'} · <FontAwesomeIcon icon={faChalkboardUser} /> <span className="rmafa-cap">{tName(f)}</span>
                            </p>
                            <div className="rmafa-tags">
                              <span className={f.IdClassStatus === 0 ? 'is-ok' : 'is-warn'}>{f.IdClassStatus === 0 ? 'Completed' : 'Not completed'}</span>
                              {f.isAutoProcessed === false && !busy && (
                                <span className="is-warn">
                                  <FontAwesomeIcon icon={faVideo} /> No Zoom data
                                </span>
                              )}
                              {f.TypeMissing && (
                                <span className="is-bad" title="No sessions of this course’s type are paid: convert or add a payment">
                                  <FontAwesomeIcon icon={faTriangleExclamation} /> {f.ClassType === 'mastery' ? 'Mastery' : 'Standard'} not paid
                                </span>
                              )}
                              {f.isEdited && (
                                <span className="is-info">
                                  <FontAwesomeIcon icon={faPenToSquare} /> Edited
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {busy ? (
                          <div className="rmafa-wait">
                            <FontAwesomeIcon icon={faRotate} spin={checkingId === f.Id} />
                            {checkingId === f.Id ? 'Checking Zoom…' : 'Waiting…'}
                          </div>
                        ) : (
                          <>
                            <div className="rmafa-times">
                              <Ring who="teacher" seconds={f.TeacherDuration} />
                              <Ring who="student" seconds={f.StudentDuration} />
                            </div>

                            <div className="rmafa-side">
                              <span className={`rmafa-pay is-${pay.tone}`}>
                                <FontAwesomeIcon icon={pay.icon} />
                                {pay.label}
                              </span>
                              <span className="rmafa-money">
                                {rate ? (
                                  <>
                                    Rate <b>{rate}</b>
                                  </>
                                ) : (
                                  <b className="is-missing">Rate not set</b>
                                )}
                                {' · '}
                                {fees ? (
                                  <>
                                    Fees <b>{fees}</b>
                                  </>
                                ) : (
                                  <b className="is-missing">Fees not set</b>
                                )}
                              </span>
                            </div>
                          </>
                        )}

                        <button type="button" className="rmafa-review" onClick={() => openReview(f)} disabled={checking || submitting}>
                          <FontAwesomeIcon icon={faPenToSquare} /> Review
                        </button>

                        {!busy && f.AdminEvaluation && (
                          <p className="rmafa-note">
                            <FontAwesomeIcon icon={faCommentDots} />
                            <span>{f.AdminEvaluation}</span>
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </>
      )}

      {/* ---------- Review sheet ---------- */}
      {review && (
        <div className="rmafa-overlay" onClick={() => !deleting && closeReview()}>
          <div className="rmafa-sheet" role="dialog" aria-modal="true" aria-label="Review feedback" onClick={(e) => e.stopPropagation()}>
            <header className="rmafa-sheet-head">
              <Photo src={review.Image} big />
              <div className="rmafa-who">
                <h4>
                  <span>{[review.FirstName, review.LastName].filter(Boolean).join(' ') || 'Student'}</span>
                </h4>
                <p>
                  {(() => {
                    const d = new Date(review.ClassDateTime);
                    return Number.isNaN(d.getTime()) ? '—' : `${fmtDay(d)} · ${fmtTime(d)}`;
                  })()}{' '}
                  · <FontAwesomeIcon icon={faChalkboardUser} /> <span className="rmafa-cap">{tName(review)}</span>
                </p>
              </div>
              <button type="button" className="rmafa-x" onClick={closeReview} aria-label="Close" disabled={deleting}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>

            <div className="rmafa-sheet-body">
              {/* class details */}
              <dl className="rmafa-dl">
                <div>
                  <dt>Module</dt>
                  <dd>{review.ModuleName || '—'}</dd>
                </div>
                <div>
                  <dt>Class</dt>
                  <dd>{review.ClassName || '—'}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd className={review.IdClassStatus === 0 ? 'is-ok' : 'is-warn'}>{review.IdClassStatus === 0 ? 'Completed' : 'Not completed'}</dd>
                </div>
              </dl>

              {/* Zoom */}
              <section className="rmafa-block">
                <h5>
                  <FontAwesomeIcon icon={faVideo} /> Zoom attendance
                </h5>
                {zoom.loading ? (
                  <div className="rmafa-skel rmafa-skel--small" />
                ) : zoom.data?.length ? (
                  <>
                    <Attendance data={zoom.data} />
                    {suggestion && (
                      <div className={`rmafa-suggest is-${payOf(suggestion.paymentType).tone}`}>
                        <FontAwesomeIcon icon={faWandMagicSparkles} />
                        <span>
                          Zoom suggests <b>{payOf(suggestion.paymentType).label}</b> · Teacher {formatDuration(suggestion.teacherDuration)} · Student{' '}
                          {formatDuration(suggestion.studentDuration)}
                        </span>
                        <button type="button" className="rmafa-link" onClick={useSuggestion}>
                          Use this
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="rmafa-muted">No Zoom data available for this class.</p>
                )}
              </section>

              {/* feedback */}
              <section className="rmafa-block">
                <h5>
                  <FontAwesomeIcon icon={faCommentDots} /> Teacher feedback
                </h5>
                <div className="rmafa-stars" aria-label={review.Rating != null ? `Rating ${review.Rating} out of 5` : 'No rating'}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <FontAwesomeIcon key={n} icon={faStar} className={n <= (review.Rating || 0) ? 'is-on' : ''} />
                  ))}
                  <span>{review.Rating != null ? `${review.Rating}/5` : 'No rating'}</span>
                </div>
                <p className="rmafa-quote">{review.Details || 'No comments provided'}</p>
              </section>

              {/* payment form */}
              <section className="rmafa-block">
                <h5>Payment</h5>
                <div className="rmafa-paypick" role="radiogroup" aria-label="Payment type">
                  {[0, 1, 2].map((v) => (
                    <label key={v} className={`is-${PAY[v].tone}${form.fullpay === v ? ' is-on' : ''}`}>
                      <input type="radio" name="fullpay" value={v} checked={form.fullpay === v} onChange={onField} />
                      <FontAwesomeIcon icon={PAY[v].icon} />
                      {PAY[v].label}
                    </label>
                  ))}
                </div>

                <div className="rmafa-grid">
                  <label className="rmafa-field">
                    <span>Session rate ($)</span>
                    <input type="number" name="SessionRate" value={form.SessionRate} onChange={onField} step="0.5" min="0" inputMode="decimal" />
                    <small>
                      {review?.RateFrom === 'teacher'
                        ? `Paid to the teacher · her ${review.ClassType === 'mastery' ? 'Mastery' : 'Standard'} rate (${review.ModuleName || 'no course'})`
                        : review?.RateFrom === 'course'
                        ? `Paid to the teacher · ${review.ModuleName || 'this course'}’s rate`
                        : 'Paid to the teacher · the student’s rate'}
                    </small>
                  </label>
                  <label className="rmafa-field">
                    <span>Session fees ($)</span>
                    <input type="number" name="SessionFees" value={form.SessionFees} onChange={onField} step="0.5" min="0" inputMode="decimal" />
                    <small>
                      {review?.FeesFrom === 'payment'
                        ? `Charged to the student · their ${review.ClassType === 'mastery' ? 'Mastery' : 'Standard'} payment ($ ÷ sessions)`
                        : 'Charged to the student · the student’s fees'}
                    </small>
                    {review?.TypeMissing && (
                      <small className="rmafa-typewarn">
                        <FontAwesomeIcon icon={faTriangleExclamation} /> {review.ModuleName || 'This course'} is {review.ClassType === 'mastery' ? 'Mastery' : 'Standard'}
                        , but no {review.ClassType === 'mastery' ? 'Mastery' : 'Standard'} sessions are paid: add a payment or convert their sessions first.
                      </small>
                    )}
                  </label>
                  <label className={`rmafa-field is-${durationTone(tSec, 'teacher')}`}>
                    <span>Teacher time (MM:SS)</span>
                    <input type="text" name="TeacherDuration" value={form.TeacherDuration} onChange={onField} placeholder="MM:SS" inputMode="numeric" />
                    <small>Target {TEACHER_TARGET}:00</small>
                  </label>
                  <label className={`rmafa-field is-${durationTone(sSec, 'student')}`}>
                    <span>Student time (MM:SS)</span>
                    <input type="text" name="StudentDuration" value={form.StudentDuration} onChange={onField} placeholder="MM:SS" inputMode="numeric" />
                    <small>Target {STUDENT_TARGET}:00</small>
                  </label>
                </div>

                <label className="rmafa-field">
                  <span>Admin note</span>
                  <textarea
                    name="AdminEvaluation"
                    value={form.AdminEvaluation}
                    onChange={onField}
                    rows={3}
                    placeholder="Why this pay, attendance issues, anything to remember…"
                  />
                </label>
              </section>
            </div>

            <footer className="rmafa-sheet-foot">
              <button type="button" className="rmafa-btn rmafa-btn--danger" onClick={askDelete} disabled={deleting}>
                <FontAwesomeIcon icon={faTrashCan} /> {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <span className="rmafa-grow" />
              <button type="button" className="rmafa-btn rmafa-btn--ghost" onClick={closeReview} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="rmafa-btn" onClick={saveReview} disabled={deleting}>
                <FontAwesomeIcon icon={reviewPay.icon} /> Save changes
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ---------- Confirm ---------- */}
      {confirm && (
        <div className="rmafa-overlay is-top" onClick={() => setConfirm(null)}>
          <div className={`rmafa-dialog is-${confirm.tone}`} role="alertdialog" aria-modal="true" aria-label={confirm.title} onClick={(e) => e.stopPropagation()}>
            <span className="rmafa-dialog-icon">
              <FontAwesomeIcon icon={confirm.tone === 'error' ? faTrashCan : confirm.tone === 'warn' ? faTriangleExclamation : faPaperPlane} />
            </span>
            <h2>{confirm.title}</h2>
            <p className="rmafa-dialog-text">{confirm.body}</p>
            <div className="rmafa-dialog-actions">
              <button type="button" className="rmafa-btn rmafa-btn--ghost" onClick={() => setConfirm(null)} autoFocus>
                Cancel
              </button>
              <button type="button" className={`rmafa-btn${confirm.tone === 'error' ? ' rmafa-btn--danger' : ''}`} onClick={confirm.onYes}>
                {confirm.label}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Result ---------- */}
      {dialog && (
        <div className="rmafa-overlay is-top" onClick={() => setDialog(null)}>
          <div className={`rmafa-dialog is-${dialog.tone}`} role="dialog" aria-modal="true" aria-label={dialog.title} onClick={(e) => e.stopPropagation()}>
            <span className="rmafa-dialog-icon">
              <FontAwesomeIcon icon={dialog.tone === 'success' ? faCircleCheck : faTriangleExclamation} />
            </span>
            <h2>{dialog.title}</h2>
            <p className="rmafa-dialog-text">{dialog.body}</p>
            <div className="rmafa-dialog-actions">
              <button type="button" className="rmafa-btn" onClick={() => setDialog(null)} autoFocus>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmafa-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default FeedbackApprove;